/**
 * The containers of a recording on its way up (UC-8, light): which one the browser's recorder
 * writes, and the extension an upload is named by — the server takes WebM, MP4/M4A (a 3GP file
 * among them), Ogg, MP3, WAV, FLAC and AMR and sniffs the bytes, so the name only has to be
 * honest. Playing a stored file is `@/utils/audio-playback`. Pure.
 */
import type { RecordingExtension } from '@/services/api/humint-recordings'
import { audioContainerOf } from '@/utils/audio-playback'

/**
 * What the recorder is asked for, best first: Opus in WebM where the browser has it, AAC in MP4
 * on Safari.
 */
const RECORDER_MIME_TYPES: readonly string[] = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
]

/** The first container the recorder supports; null leaves the choice to the browser. */
export function pickRecorderMimeType(isTypeSupported: (type: string) => boolean): string | null {
  return RECORDER_MIME_TYPES.find(type => isTypeSupported(type)) ?? null
}

/** A picked `.webm`, `.mp4` or `.3gp` arrives typed as video; an Ogg file as `application/ogg`. */
const EXTENSION_OF_TYPE: ReadonlyMap<string, RecordingExtension> = new Map([
  ['audio/webm', 'webm'],
  ['video/webm', 'webm'],
  ['audio/mp4', 'm4a'],
  ['audio/m4a', 'm4a'],
  ['audio/x-m4a', 'm4a'],
  ['video/mp4', 'm4a'],
  ['audio/3gpp', 'm4a'],
  ['video/3gpp', 'm4a'],
  ['audio/ogg', 'ogg'],
  ['audio/opus', 'ogg'],
  ['application/ogg', 'ogg'],
  ['audio/mpeg', 'mp3'],
  ['audio/mp3', 'mp3'],
  ['audio/wav', 'wav'],
  ['audio/wave', 'wav'],
  ['audio/x-wav', 'wav'],
  ['audio/vnd.wave', 'wav'],
  ['audio/flac', 'flac'],
  ['audio/x-flac', 'flac'],
  ['audio/amr', 'amr'],
  ['audio/amr-wb', 'amr'],
])

/** A MIME type without its parameters: `audio/webm;codecs=opus` → `audio/webm`. */
const bareType = (type: string): string => type.split(';')[0].trim().toLowerCase()

/**
 * The extension an upload is named by: the one its MIME type names, else the one its file name
 * carries (desktop Chrome may send no type); null for a container the server does not take.
 */
export function uploadExtension(source: {
  type?: string | null
  name?: string | null
}): RecordingExtension | null {
  const byType = source.type ? EXTENSION_OF_TYPE.get(bareType(source.type)) : undefined
  // The containers a stored file plays as are the ones the server takes.
  return byType ?? (source.name ? audioContainerOf(source.name) : null)
}
